import { formatMoney } from '@expenditure/core';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { useCurrentUser } from '../auth/AuthProvider';
import { GroupRow } from '../components/GroupRow';
import { Icon } from '../components/Icon';
import { Avatar, CategoryIcon, EmptyState, ErrorState, HeroAmount, Money, PageSkeleton, SectionTitle } from '../components/ui';
import { errorMessage, formatDayHeader } from '../lib/format';
import { useDashboard } from '../lib/queries';
import { haptic } from '../lib/telegram';

export function DashboardPage() {
  const { t, i18n } = useTranslation();
  const user = useCurrentUser();
  const navigate = useNavigate();
  const { data, isPending, error, refetch } = useDashboard();

  if (isPending) return <PageSkeleton />;
  if (error) return <ErrorState message={errorMessage(error, t)} onRetry={() => void refetch()} />;

  const [primary, ...others] = data.totals;

  return (
    <main className="page page--home app">
      <section className="hero hero--top" aria-label={t('dashboard.yourBalance')}>
        <div className="hero-cards" aria-hidden>
          <i />
          <i />
          <i />
        </div>
        <div className="hero-top">
          <div className="row" style={{ minWidth: 0 }}>
            <Avatar name={user.fullName} photo={user.profilePhoto} small />
            {/* Full name; long names are cut with an ellipsis instead of wrapping. */}
            <div className="small ellipsis" style={{ color: 'var(--hero-muted)' }} title={user.fullName}>
              {t('dashboard.greeting', { name: user.fullName })}
            </div>
          </div>
          <Link className="pill-btn pill-btn--glass" to="/groups" style={{ height: 32, fontSize: 13 }}>
            {t('dashboard.yourGroups')}
          </Link>
        </div>

        <div className="hero-label">{t('dashboard.yourBalance')}</div>
        <HeroAmount
          minor={primary?.netMinor ?? 0}
          currency={primary?.currency ?? user.defaultCurrency}
          signed={(primary?.netMinor ?? 0) !== 0}
        />
        {(!primary || primary.netMinor === 0) && (
          <div className="hero-label" style={{ marginTop: 4 }}>
            {t('dashboard.allSettled')}
          </div>
        )}

        <div className="hero-actions">
          <button className="pill-btn" onClick={() => (haptic.tap(), void navigate('/add'))}>
            <span className="pill-icon">
              <Icon name="plus" size={16} />
            </span>
            {t('dashboard.addExpense')}
          </button>
          <button className="pill-btn pill-btn--glass" onClick={() => (haptic.tap(), void navigate('/groups/new'))}>
            {t('dashboard.newGroup')}
          </button>
        </div>

        <div className="glass-card">
          <div className="glass-stat">
            <div className="hero-label">{t('dashboard.youAreOwed')}</div>
            <strong className="num">{formatMoney(primary?.owedToMeMinor ?? 0, primary?.currency ?? user.defaultCurrency)}</strong>
          </div>
          <div className="glass-stat">
            <div className="hero-label">{t('dashboard.youOwe')}</div>
            <strong className="num">{formatMoney(primary?.iOweMinor ?? 0, primary?.currency ?? user.defaultCurrency)}</strong>
          </div>
          {data.groups.length > 0 && (
            <div className="avatar-stack" aria-hidden>
              {data.groups.slice(0, 3).map((g) => (
                <Avatar key={g.id} name={g.name} small />
              ))}
              {data.groups.length > 3 && <span className="avatar-more">+{data.groups.length - 3}</span>}
            </div>
          )}
        </div>

        {others.length > 0 && (
          <div className="hero-sub">
            {others.map((o) => (
              <div className="hero-pill" key={o.currency}>
                {o.currency}
                <strong className="num">{formatMoney(o.netMinor, o.currency, { signed: true })}</strong>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="panel">
        <div className="panel-header">
          <Link className="icon-btn" to="/groups" aria-label={t('dashboard.yourGroups')}>
            <Icon name="list" size={18} />
          </Link>
          <h2>{t('dashboard.recentActivity')}</h2>
          <Link className="icon-btn" to="/add" aria-label={t('dashboard.addExpense')}>
            <Icon name="plus" size={18} />
          </Link>
        </div>

        {data.groups.length === 0 ? (
          <EmptyState
            icon="inbox"
            title={t('dashboard.emptyTitle')}
            body={t('dashboard.emptyBody')}
            action={
              <Link className="btn btn--primary" to="/groups/new">
                {t('groups.create')}
              </Link>
            }
          />
        ) : (
          <>
            <div className="list">
              {data.recentActivity.length === 0 && <div className="empty">{t('dashboard.noActivity')}</div>}
              {data.recentActivity.map((a) => {
                const lent = a.paidByMe ? a.amountMinor - a.myShareMinor : 0;
                return (
                  <Link key={a.id} className="list-row" to={`/groups/${a.groupId}/expenses/${a.id}`}>
                    <CategoryIcon category={a.category} />
                    <div className="grow">
                      <div className="list-title ellipsis">{a.description}</div>
                      <div className="list-sub ellipsis">
                        {a.groupName} · {formatDayHeader(a.expenseDate, i18n.language, t)}
                      </div>
                    </div>
                    <div className="list-end">
                      {a.paidByMe ? (
                        <span className="list-title pos num">+{formatMoney(lent, a.currency)}</span>
                      ) : a.myShareMinor > 0 ? (
                        <span className="list-title neg num">-{formatMoney(a.myShareMinor, a.currency)}</span>
                      ) : (
                        <Money minor={a.amountMinor} currency={a.currency} className="list-title" />
                      )}
                      <div className="small muted">
                        {a.paidByMe ? t('group.youLent') : a.myShareMinor > 0 ? t('group.youBorrowed') : t('group.notInvolved')}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>

            <SectionTitle action={<Link to="/groups">{t('dashboard.seeAll')}</Link>}>
              {t('dashboard.yourGroups')}
            </SectionTitle>
            <div className="list">
              {data.groups.slice(0, 3).map((g) => (
                <GroupRow key={g.id} group={g} />
              ))}
            </div>
          </>
        )}
      </section>
    </main>
  );
}
